<template>
  <div class="ers" :class="{ 'ers--open': open }" :style="{ width: width }">
    <button type="button" class="ers__trigger" @click="toggle">
      <span
        class="ers__value"
        :class="{ 'ers__value--placeholder': isEmpty }"
        :style="{ textAlign: align }"
      >
        {{ displayValue }}
      </span>
      <v-icon
        size="18"
        class="ers__chev"
        :class="{ 'ers__chev--open': open }"
        :color="open ? '#0ea5e9' : '#94a3b8'"
      >
        mdi-chevron-down
      </v-icon>
    </button>

    <transition name="ers-pop">
      <div v-show="open" class="ers__panel">
        <!-- Autocomplete search -->
        <div v-if="searchable" class="ers__search">
          <v-icon size="16" color="var(--app-text-faint)">mdi-magnify</v-icon>
          <input
            ref="search"
            v-model="query"
            type="text"
            class="ers__search-input"
            :placeholder="searchPlaceholder"
            @keydown.esc.prevent="open = false"
            @keydown.enter.prevent="selectFirst"
          />
          <v-icon
            v-if="query"
            size="16"
            color="var(--app-text-faint)"
            class="ers__search-clear"
            @click="query = ''"
          >
            mdi-close-circle
          </v-icon>
        </div>

        <ul class="ers__list">
          <template v-for="(g, gi) in filteredGroups">
            <li v-if="g.label" :key="'g-' + gi" class="ers__group">
              {{ g.label }}
            </li>
            <li
              v-for="opt in g.items"
              :key="'o-' + gi + '-' + String(opt)"
              class="ers__option"
              :class="{ 'ers__option--selected': opt === value }"
              @click="select(opt)"
            >
              <span>{{ opt }}</span>
              <v-icon v-if="opt === value" size="15" color="var(--app-accent)">
                mdi-check
              </v-icon>
            </li>
          </template>
          <li v-if="!flatFiltered.length" class="ers__empty">
            No matches
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * ErSelect — themed custom dropdown for the Email Reminder settings page.
 * Replaces the native <select> so the option list matches the project theme.
 * Pass `searchable` to turn it into an autocomplete (type-to-filter) dropdown.
 * Usage: <er-select v-model="x" :options="[...]" width="120px" searchable />
 *
 * `options` accepts either a flat array of strings, or grouped options:
 *   [{ group: "Clients", items: ["A", "B"] }, { group: "HRDC Corp", items: [...] }]
 * Grouped options render a small section header above each group's items.
 */
export default {
  name: "ErSelect",
  props: {
    value: { type: [String, Number], default: "" },
    options: { type: Array, default: () => [] },
    width: { type: String, default: "100%" },
    align: { type: String, default: "left" },
    placeholder: { type: String, default: "Select" },
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: "Search…" },
  },
  data: () => ({
    open: false,
    query: "",
  }),
  computed: {
    isEmpty() {
      return this.value === "" || this.value === null || this.value === undefined;
    },
    displayValue() {
      return this.isEmpty ? this.placeholder : String(this.value);
    },
    // True when `options` is a grouped structure ([{ group, items }, ...]).
    isGrouped() {
      const first = this.options[0];
      return (
        this.options.length > 0 &&
        typeof first === "object" &&
        first !== null &&
        Array.isArray(first.items)
      );
    },
    // Normalize to [{ label, items }]. A flat string array becomes one
    // unlabeled group, so existing (ungrouped) usages render exactly as before.
    groups() {
      if (this.isGrouped) {
        return this.options.map((g) => ({
          label: g.group || g.label || "",
          items: g.items || [],
        }));
      }
      return [{ label: "", items: this.options }];
    },
    // Groups after applying the search filter; empty groups are dropped.
    filteredGroups() {
      const q = this.searchable ? this.query.trim().toLowerCase() : "";
      return this.groups
        .map((g) => ({
          label: g.label,
          items: q
            ? g.items.filter((opt) => String(opt).toLowerCase().includes(q))
            : g.items,
        }))
        .filter((g) => g.items.length);
    },
    flatFiltered() {
      return this.filteredGroups.reduce((acc, g) => acc.concat(g.items), []);
    },
  },
  methods: {
    toggle() {
      this.open = !this.open;
      if (this.open) {
        this.query = "";
        this.$nextTick(() => {
          if (this.searchable && this.$refs.search) this.$refs.search.focus();
          const el = this.$el.querySelector(".ers__option--selected");
          if (el) el.scrollIntoView({ block: "nearest" });
        });
      }
    },
    select(opt) {
      this.$emit("input", opt);
      this.open = false;
    },
    selectFirst() {
      if (this.flatFiltered.length) this.select(this.flatFiltered[0]);
    },
    onDocPointer(e) {
      if (this.open && !this.$el.contains(e.target)) {
        this.open = false;
      }
    },
  },
  mounted() {
    document.addEventListener("mousedown", this.onDocPointer);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.onDocPointer);
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .ers__trigger {
  background: var(--app-card);
  border: 1px solid var(--app-border);
}

.dark-mode .ers__trigger:hover {
  border-color: var(--app-border-strong);
  background: var(--app-surface);
}

.dark-mode .ers--open .ers__trigger {
  background: var(--app-card);
}

.dark-mode .ers__value {
  color: var(--app-text);
}

.dark-mode .ers__value--placeholder {
  color: var(--app-text-faint);
}

.dark-mode .ers__panel {
  background: var(--app-card);
  border: 1px solid var(--app-border);
}

.dark-mode .ers__search {
  border-bottom: 1px solid var(--app-border);
  background: var(--app-surface);
}

.dark-mode .ers__search-input {
  color: var(--app-text);
}

.dark-mode .ers__search-input::placeholder {
  color: var(--app-text-faint);
}

.dark-mode .ers__group {
  color: var(--app-text-faint);
}

.dark-mode .ers__option {
  color: var(--app-text);
}

.dark-mode .ers__option:hover {
  background: var(--app-accent-soft);
}

.dark-mode .ers__option--selected {
  background: var(--app-accent-soft);
}

.dark-mode .ers__empty {
  color: var(--app-text-faint);
}

.dark-mode .ers__list::-webkit-scrollbar-thumb {
  background: var(--app-elevated);
  border: 2px solid var(--app-card);
}

/* >>> end auto-dark <<< */

.ers {
  position: relative;
  font-family: "Lato", sans-serif;
}

/* Trigger */
.ers__trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  height: 36px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0 8px 0 12px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.ers__trigger:hover {
  border-color: #94a3b8;
  background: #f8fafc;
}
.ers--open .ers__trigger {
  border-color: #0ea5e9;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.12);
}
.ers__value {
  flex: 1;
  font-size: 0.84rem;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ers__value--placeholder {
  color: #94a3b8;
  font-weight: 500;
}
.ers__chev {
  flex-shrink: 0;
  transition: transform 0.15s ease;
}
.ers__chev--open {
  transform: rotate(180deg);
}

/* Panel */
.ers__panel {
  position: absolute;
  z-index: 30;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.14);
  overflow: hidden;
}

/* Search box */
.ers__search {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-bottom: 1px solid #f1f5f9;
  background: #f8fafc;
}
.ers__search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-family: "Lato", sans-serif;
  font-size: 0.83rem;
  font-weight: 600;
  color: #0f172a;
  min-width: 0;
}
.ers__search-input::placeholder {
  color: #94a3b8;
  font-weight: 500;
}
.ers__search-clear {
  cursor: pointer;
}

/* Option list */
.ers__list {
  margin: 0;
  padding: 5px;
  list-style: none;
  max-height: 224px;
  overflow-y: auto;
}
.ers__group {
  padding: 8px 10px 4px;
  font-size: 0.62rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
  user-select: none;
}
.ers__group + .ers__option {
  margin-top: 0;
}
.ers__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 10px;
  border-radius: 7px;
  font-size: 0.83rem;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}
.ers__option:hover {
  background: #f0f9ff;
  color: #0369a1;
}
.ers__option--selected {
  background: #e0f2fe;
  color: #0369a1;
  font-weight: 800;
}
.ers__option--selected:hover {
  background: #d6ecfb;
}
.ers__empty {
  padding: 12px 10px;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: #94a3b8;
}

/* Pop transition */
.ers-pop-enter-active,
.ers-pop-leave-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
}
.ers-pop-enter,
.ers-pop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Scrollbar */
.ers__list::-webkit-scrollbar {
  width: 8px;
}
.ers__list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 8px;
  border: 2px solid #fff;
}
.ers__list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
